import {
  DOCUMENT_READING_LABEL,
  DOCUMENTS_PER_QUESTION,
  type ResearchDocument,
} from '@buakei/types';
import { Button, Notice } from '@buakei/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, FileUp, FlaskConical, Trash2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { api, researchDocumentsApi, type TokenSource } from '@/services/api';

/**
 * Documents for a research question (ADR-045): the user's own, in this professional workspace,
 * private to them. Chosen documents are read by Ask Buakei for each question while they stay
 * chosen. Shown only when document uploads are switched on. The composer's "+" opens the panel;
 * chosen documents show as chips inside the composer.
 */

const ACCEPT = '.pdf,.docx,.txt,application/pdf,text/plain';
const size = (bytes: number) =>
  bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/** Whether uploads are switched on, and whether they are still in testing (ADR-044). */
export function useDocumentUploads() {
  const flags = useQuery({ queryKey: ['feature-flags'], queryFn: api.featureFlags });
  const state = flags.data?.documentUploads;
  return { enabled: state !== undefined && state !== 'unavailable', live: state === 'live' };
}

function useResearchDocuments(tokens: TokenSource, workspaceId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['research-documents', workspaceId],
    queryFn: () => researchDocumentsApi.list(tokens, workspaceId),
    enabled,
  });
}

/** The chosen documents, inside the composer above the question. */
export function QuestionDocumentChips({
  tokens,
  workspaceId,
  selected,
  onChange,
}: {
  tokens: TokenSource;
  workspaceId: string;
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  const { enabled } = useDocumentUploads();
  const list = useResearchDocuments(tokens, workspaceId, enabled);
  const chosen = (list.data?.documents ?? []).filter((d) => selected.includes(d.id));
  if (!enabled || chosen.length === 0) return null;
  return (
    <ul className="flex flex-wrap gap-2 px-1 pt-1" aria-label="Documents for this question">
      {chosen.map((d) => (
        <li
          key={d.id}
          className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-line-200 bg-navy-50 py-1 pr-1 pl-2 text-xs text-ink-900"
        >
          <FileText className="size-3.5 shrink-0 text-navy-900" aria-hidden />
          <span className="max-w-[14rem] truncate">{d.name}</span>
          <button
            type="button"
            aria-label={`Stop using ${d.name}`}
            className="rounded p-0.5 text-muted-600 hover:bg-white hover:text-navy-900"
            onClick={() => onChange(selected.filter((s) => s !== d.id))}
          >
            <X className="size-3" aria-hidden />
          </button>
        </li>
      ))}
    </ul>
  );
}

/** The documents panel, opened from the composer's "+"; it floats above the composer. */
export function QuestionDocumentsPanel({
  id,
  tokens,
  workspaceId,
  selected,
  onChange,
  onClose,
}: {
  id: string;
  tokens: TokenSource;
  workspaceId: string;
  selected: string[];
  onChange: (ids: string[]) => void;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { enabled, live } = useDocumentUploads();
  const list = useResearchDocuments(tokens, workspaceId, enabled);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['research-documents', workspaceId] });

  useEffect(() => {
    panel.current?.focus();
  }, []);
  // Escape closes the panel, wherever focus is inside it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && panel.current?.contains(document.activeElement)) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      const added: ResearchDocument[] = [];
      for (const [i, file] of files.entries()) {
        if (list.data && file.size > list.data.maxBytes) {
          throw new Error(`${file.name} is larger than 50 MB.`);
        }
        setProgress(`Adding ${i + 1} of ${files.length}: ${file.name}…`);
        added.push(await researchDocumentsApi.upload(tokens, workspaceId, file));
      }
      return added;
    },
    onSuccess: (added) => {
      // New readable documents are chosen for the next question, up to the limit.
      const readable = added.filter((d) => d.reading === 'read').map((d) => d.id);
      onChange([...selected, ...readable].slice(0, DOCUMENTS_PER_QUESTION));
    },
    onSettled: () => {
      setProgress(null);
      if (input.current) input.current.value = '';
      void refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (doc: string) => researchDocumentsApi.remove(tokens, workspaceId, doc),
    onSuccess: (_r, doc) => onChange(selected.filter((s) => s !== doc)),
    onSettled: () => {
      setConfirming(null);
      void refresh();
    },
  });

  const docs = list.data?.documents ?? [];
  const toggle = (doc: string) =>
    onChange(selected.includes(doc) ? selected.filter((s) => s !== doc) : [...selected, doc]);
  const error = upload.error ?? remove.error ?? list.error;

  return (
    <div
      id={id}
      ref={panel}
      role="dialog"
      aria-label="Documents for your questions"
      tabIndex={-1}
      className="absolute bottom-full left-0 z-20 mb-2 flex max-h-[min(28rem,60dvh)] w-full flex-col gap-3 overflow-y-auto rounded-lg border border-line-200 bg-white p-4 shadow-lg focus:outline-none"
    >
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-ink-900">
          Choose up to {DOCUMENTS_PER_QUESTION} documents for Buakei to read with your questions:
          PDF, Word (.docx) or text, up to 50 MB each. They are private to you in this workspace,
          deleted 24 months after a question last reads them, and never treated as legal authority.
        </p>
        <Button type="button" variant="ghost" size="sm" aria-label="Close" onClick={onClose}>
          <X className="size-4" aria-hidden />
        </Button>
      </div>
      {!live ? (
        <Notice title="Testing only: use made-up documents" icon={FlaskConical}>
          <p>
            Uploads are switched on for testing before the confidentiality checks are complete. Do
            not add real client documents or anything confidential.
          </p>
        </Notice>
      ) : null}
      {list.isLoading ? <p className="text-sm text-muted-600">Loading documents…</p> : null}
      {list.data && docs.length === 0 ? (
        <p className="text-sm text-muted-600">No documents yet.</p>
      ) : null}
      {docs.length > 0 ? (
        <ul className="flex flex-col gap-1" aria-label="Your research documents">
          {docs.map((d) => {
            const atLimit = !selected.includes(d.id) && selected.length >= DOCUMENTS_PER_QUESTION;
            return (
              <li key={d.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                <label className="flex min-w-0 flex-1 items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selected.includes(d.id)}
                    disabled={d.reading !== 'read' || atLimit}
                    onChange={() => toggle(d.id)}
                    className="accent-navy-900"
                  />
                  <span className="truncate text-navy-900">{d.name}</span>
                  <span className="shrink-0 text-xs text-muted-600">
                    {size(d.size)}
                    {d.pages !== null ? ` · ${d.pages} pages` : ''} ·{' '}
                    {DOCUMENT_READING_LABEL[d.reading]}
                  </span>
                </label>
                {confirming === d.id ? (
                  <span className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(d.id)}
                    >
                      Delete permanently
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setConfirming(null)}
                    >
                      Keep
                    </Button>
                  </span>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label={`Delete ${d.name}`}
                    onClick={() => setConfirming(d.id)}
                  >
                    <Trash2 className="size-4" aria-hidden />
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      ) : null}
      <div className="flex flex-wrap items-center gap-3">
        <input
          ref={input}
          id={`${id}-file`}
          type="file"
          multiple
          accept={ACCEPT}
          aria-label="Choose documents to add"
          tabIndex={-1}
          className="sr-only"
          onChange={(e) => {
            const files = [...(e.target.files ?? [])];
            if (files.length) upload.mutate(files);
          }}
        />
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={upload.isPending || !list.data || docs.length >= (list.data?.limit ?? 0)}
          onClick={() => input.current?.click()}
        >
          <FileUp className="size-4" aria-hidden />
          {progress ?? 'Upload documents'}
        </Button>
      </div>
      {error ? (
        <p role="alert" className="text-sm text-ink-900">
          {error.message}
        </p>
      ) : null}
    </div>
  );
}
